import { SvgIcon, SvgIconProps } from '@material-ui/core';
import { themeColors } from '../../theme/design-colors';

// Color variants for Daimler Truck icons as per design guidelines
// Icons can be used in colors: black, white, Petrol, Light Grey (with variations)
export const DaimlerIconColors = {
  black: themeColors.black,
  white: themeColors.white,
  petrol: themeColors.petrol,
  petrolDark: themeColors.petrol20k,
  petrolLight: themeColors.petrol60,
  lightGrey: themeColors.lightGrey60k,
  lightGreyDark: themeColors.lightGrey80k,
  lightGreyLight: themeColors.lightGrey40k,
} as const;

// Base Daimler Truck icon component following official design guidelines
// Angular design idiom with line thickness based on Daimler CS font
// Uniform grid system for positioning, two-dimensional without shadows
export const DaimlerIcon = ({ children, ...props }: SvgIconProps) => (
  <SvgIcon
    {...props}
    style={{
      // Angular design approach - no rounded caps/joins
      strokeWidth: '2px', // Thicker lines based on Daimler CS font
      strokeLinecap: 'square', // Angular, not rounded
      strokeLinejoin: 'miter', // Sharp corners, not rounded
      fill: 'none',
      stroke: 'currentColor',
      // Uniform grid system - ensure consistent positioning
      width: '24px', // Standard grid unit
      height: '24px', // Standard grid unit
      // Ensure crisp rendering on pixel grid
      shapeRendering: 'crispEdges',
      imageRendering: 'pixelated',
      // Snap to pixel grid for consistent alignment
      transform: 'translateZ(0)',
      ...props.style,
    }}
    viewBox="0 0 24 24" // Standard 24x24 grid
  >
    {children}
  </SvgIcon>
);

// Home Icon - Angular design with geometric shapes
export const DaimlerHomeIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <polygon points="12,2 21,9 21,20 16,20 16,14 8,14 8,20 3,20 3,9" />
    <rect x="9" y="15" width="6" height="5" />
  </DaimlerIcon>
);

// Catalog/Component Icon - Grid-based modular design
export const DaimlerCatalogIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <rect x="2" y="2" width="8" height="8" />
    <rect x="14" y="2" width="8" height="8" />
    <rect x="14" y="14" width="8" height="8" />
    <rect x="2" y="14" width="8" height="8" />
  </DaimlerIcon>
);

// API/Technical Icon - Angular technical interface design
export const DaimlerAPIIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <rect x="2" y="2" width="20" height="20" />
    <polygon points="6,8 10,8 10,16 6,16" />
    <polygon points="14,8 18,8 18,16 14,16" />
    <rect x="11" y="10" width="2" height="4" />
  </DaimlerIcon>
);

// Documentation Icon - Angular document design
export const DaimlerDocsIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <polygon points="6,2 18,2 20,4 20,22 4,22 4,4" />
    <polygon points="18,2 18,6 22,6" />
    <rect x="7" y="8" width="10" height="2" />
    <rect x="7" y="12" width="10" height="2" />
    <rect x="7" y="16" width="6" height="2" />
  </DaimlerIcon>
);

// Create/Add Icon - Angular plus design
export const DaimlerCreateIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <rect x="2" y="2" width="20" height="20" />
    <rect x="10" y="6" width="4" height="12" />
    <rect x="6" y="10" width="12" height="4" />
  </DaimlerIcon>
);

// Search Icon - Angular magnifying glass design
export const DaimlerSearchIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <rect x="2" y="2" width="14" height="14" transform="rotate(45 9 9)" />
    <rect x="16" y="16" width="6" height="2" transform="rotate(45 19 17)" />
  </DaimlerIcon>
);

// Group/People Icon - Angular team representation
export const DaimlerGroupIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <polygon points="6,4 10,4 12,6 10,10 6,10 4,6" />
    <polygon points="14,4 18,4 20,6 18,10 14,10 12,6" />
    <rect x="2" y="16" width="8" height="6" />
    <rect x="14" y="16" width="8" height="6" />
  </DaimlerIcon>
);

// Menu/Navigation Icon - Angular three bars
export const DaimlerMenuIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <rect x="2" y="5" width="20" height="3" />
    <rect x="2" y="11" width="20" height="3" />
    <rect x="2" y="17" width="20" height="3" />
  </DaimlerIcon>
);

// Settings/Gear Icon - Angular industrial gear design
export const DaimlerSettingsIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <polygon points="12,2 14,6 18,6 20,8 16,10 18,14 16,18 14,18 12,22 10,18 6,18 4,16 8,14 6,10 8,6 10,6" />
    <rect x="9" y="9" width="6" height="6" />
  </DaimlerIcon>
);

// Notification/Bell Icon - Angular alert design
export const DaimlerNotificationIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <polygon points="12,2 8,6 8,14 4,18 20,18 16,14 16,6" />
    <rect x="10" y="19" width="4" height="3" />
  </DaimlerIcon>
);

// Truck-specific Icon for Daimler Truck branding - Angular design
export const DaimlerTruckIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <rect x="1" y="4" width="14" height="10" />
    <polygon points="15,6 19,6 21,8 21,14 15,14" />
    <rect x="3" y="16" width="4" height="4" />
    <rect x="17" y="16" width="4" height="4" />
    <rect x="8" y="14" width="2" height="2" />
    <rect x="14" y="14" width="2" height="2" />
  </DaimlerIcon>
);

// Build/Tools Icon - Angular wrench and hammer design
export const DaimlerBuildIcon = (props: SvgIconProps) => (
  <DaimlerIcon {...props}>
    <polygon points="2,20 8,14 10,16 4,22" />
    <rect x="6" y="12" width="4" height="8" transform="rotate(-45 8 16)" />
    <polygon points="22,2 16,8 14,6 20,0" />
    <rect x="14" y="4" width="8" height="4" transform="rotate(45 18 6)" />
    <rect x="10" y="10" width="4" height="4" />
  </DaimlerIcon>
);

// Export all icons
export const DaimlerIcons = {
  Home: DaimlerHomeIcon,
  Catalog: DaimlerCatalogIcon,
  API: DaimlerAPIIcon,
  Docs: DaimlerDocsIcon,
  Create: DaimlerCreateIcon,
  Search: DaimlerSearchIcon,
  Group: DaimlerGroupIcon,
  Menu: DaimlerMenuIcon,
  Settings: DaimlerSettingsIcon,
  Notification: DaimlerNotificationIcon,
  Truck: DaimlerTruckIcon,
  Build: DaimlerBuildIcon,
};
